
import Vue from 'vue';
import { mapState } from 'vuex';
import _isNil from 'lodash/isNil';
import _toString from 'lodash/toString';
import _trim from 'lodash/trim';
import BigNumber from 'bignumber.js';
import moment from 'moment-timezone';
import { MyBalanceMixin } from '@/mixins';
import InfoMessage from '@/components/InfoMessage.vue';
import TokensSVG from '@/assets/TokensSVG.vue';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { calcRdBalanceToRunnitTokens, calcRunnitTokensToRdBalance } from '@run-diffusion/shared';

export default Vue.extend({
	name: 'TimeYourSessionInputs',
	mixins: [
		MyBalanceMixin,
	],
	props: {
		allowAnyTimeSelection: { type: Boolean, default: false },
		maxHoursLimit: { type: Number, default: 168 },
		highestDefaultValueAllowed: { type: Number, default: 2 },
		hideEstimatedTotal: { type: Boolean, default: false },
		previewMode: { type: Boolean, default: false },
		runnitsOwnerSelection: { type: String, default: null },
		insufficientBalanceMessage: { type: String, default: null },
		title: { type: String, default: null },
		purchasePrice: { type: Number, default: 0 },
		disabled: { type: Boolean, default: false },
		selectedScheduledStopInHrs: { type: Number, default: 0 },
		selectedScheduledStopInMins: { type: Number, default: 0 },
		extendingFromScheduledStopAt: { type: Object, default: null },
		canGoNegative: { type: Boolean, default: false },
	},
	data () {
		return {
			scheduledStopInHrsItems: [],
			scheduledStopInMinsItems: [],
			NEGATIVE_HOURS_LIMIT: 2,
			INCREMENT_UNIT: 15,
			MAX_SUB_60_INCREMENT_UNIT: 45,

			allBalanceMaxHrs: 0,
			allBalanceMaxMins: 0,

			showRadioVal1Hr: false,
			showRadioVal2Hr: false,
			showRadioVal3Hr: false,
			showRadioValAll: false,
			showRadioValCustom: false,
			RADIO_VAL_ALL_MAX_HRS: 25, // too much to risk letting them waste so much money with one-click
			RADIO_VAL_1_HR: 1,
			RADIO_VAL_2_HR: 2,
			RADIO_VAL_3_HR: 3,
			RADIO_VAL_ALL: 4,
			RADIO_VAL_CUSTOM: 5,
			radioGroupVal: 2,

			// FORM
			formValid: true,
			rules: {
				required: [
					v => !!_trim(v) || 'Required',
				],
			},
		};
	},
	computed: {
		...mapState([
			'runnitState',
		]),
		currRunnitsOwnerSelection () {
			// Prefer prop if provided (for static cases), otherwise use Vuex state
			if (this.runnitsOwnerSelection) {
				return this.runnitsOwnerSelection;
			}
			return (this.runnitState && this.runnitState.runnitsOwnerSelection) || RUNNITS_OWNER_SELECTION.USER;
		},
		selectedComputedBalance () {
			return this.currRunnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
				? this.computedTeamBalance
				: this.computedUserBalance;
		},
		selectedTokensBalanceData () {
			return this.currRunnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
				? this.teamBalanceAccountRunnitTokensData
				: this.userBalanceAccountRunnitTokensData;
		},
		selectedTokensBalance () {
			// Paid runnit tokens only (in hundredths). Free tokens are not used for sessions.
			return this.selectedTokensBalanceData?.runnitTokens || 0;
		},
		selectedTokensBalanceInCents () {
			// Convert tokens (in hundredths) to equivalent cents value
			return calcRunnitTokensToRdBalance(this.selectedTokensBalance, false);
		},
		combinedBalanceInCents () {
			// Open-Source balance + token value (both in cents)
			return this.selectedComputedBalance + this.selectedTokensBalanceInCents;
		},
		radioGroupHasNoOptions () {
			return !(
				this.showRadioVal1Hr ||
				this.showRadioVal2Hr ||
				this.showRadioVal3Hr ||
				this.showRadioValAll ||
				this.showRadioValCustom
			);
		},
		usingAllBalance () {
			return this.radioGroupVal === this.RADIO_VAL_ALL;
		},
		calculatedMins () {
			if (
				this.selectedScheduledStopInHrs ||
				this.selectedScheduledStopInMins
			) {
				return (this.selectedScheduledStopInHrs * 60) + this.selectedScheduledStopInMins;
			}
			return 0;
		},
		estimatedTotal () {
			if (
				(
					this.selectedScheduledStopInHrs ||
					this.selectedScheduledStopInMins
				) &&
				this.purchasePrice > 0 &&
				this.calculatedMins >= 5
			) {
				const purchaseHrsDecimal: BigNumber = new BigNumber(this.calculatedMins).div(60);
				const hrPrice: number = this.purchasePrice;
				const cost: BigNumber = purchaseHrsDecimal.times(hrPrice);
				return cost.integerValue(BigNumber.ROUND_HALF_UP).toNumber();
			}
			return 0;
		},
		estimatedOpenSourceBalanceTotal () {
			const total: number = this.estimatedTotal;
			if (total === 0) return 0;
			// Use Open-Source balance first, up to the total cost
			return Math.min(total, this.selectedComputedBalance);
		},
		estimatedTokensTotal () {
			const total: number = this.estimatedTotal;
			const openSourceUsed: number = this.estimatedOpenSourceBalanceTotal;
			const remainingCost: number = total - openSourceUsed;
			if (remainingCost <= 0) return 0;
			// Convert remaining cost in cents to tokens (in hundredths)
			return calcRdBalanceToRunnitTokens(remainingCost, true);
		},
		newExtendedScheduledStopAtMoment () {
			if (!this.extendingFromScheduledStopAt) return null;
			return moment(this.extendingFromScheduledStopAt.toMillis())
				.add(this.calculatedMins, 'minutes')
				.toDate();
		},
	},
	created () {
		this.setRadioSelectionsState(this.combinedBalanceInCents, this.purchasePrice);
		this.initScheduledStopInHrsItems(this.combinedBalanceInCents, this.purchasePrice);
		this.initScheduledStopInMinsItems(this.selectedScheduledStopInHrs, this.combinedBalanceInCents, this.purchasePrice);
		this.syncRadioGroupVal(this.selectedScheduledStopInHrs, this.selectedScheduledStopInMins);
	},
	watch: {
		purchasePrice (newVal: number) {
			this.setRadioSelectionsState(this.combinedBalanceInCents, newVal);
			this.initScheduledStopInHrsItems(this.combinedBalanceInCents, newVal);
			this.initScheduledStopInMinsItems(this.selectedScheduledStopInHrs, this.combinedBalanceInCents, newVal);
		},
		combinedBalanceInCents (newVal: number) {
			this.setRadioSelectionsState(newVal, this.purchasePrice);
			this.initScheduledStopInHrsItems(newVal, this.purchasePrice);
			this.initScheduledStopInMinsItems(this.selectedScheduledStopInHrs, newVal, this.purchasePrice);
		},
		selectedScheduledStopInHrs (newVal: number) {
			this.initScheduledStopInMinsItems(newVal, this.combinedBalanceInCents, this.purchasePrice);
			this.syncRadioGroupVal(newVal, this.selectedScheduledStopInMins);
		},
		selectedScheduledStopInMins (newVal: number) {
			this.syncRadioGroupVal(this.selectedScheduledStopInHrs, newVal);
		},
	},
	methods: {
		onRadioGroupValChange (val) {
			this.radioGroupVal = _isNil(val) ? this.radioGroupVal : val;
			switch (this.radioGroupVal) {
				case this.RADIO_VAL_1_HR:
					this.setSelectedScheduledStopInHrs(1);
					this.setSelectedScheduledStopInMins(0);
					break;
				case this.RADIO_VAL_2_HR:
					this.setSelectedScheduledStopInHrs(2);
					this.setSelectedScheduledStopInMins(0);
					break;
				case this.RADIO_VAL_3_HR:
					this.setSelectedScheduledStopInHrs(3);
					this.setSelectedScheduledStopInMins(0);
					break;
				case this.RADIO_VAL_ALL:
					this.setSelectedScheduledStopInHrs(this.allBalanceMaxHrs);
					this.setSelectedScheduledStopInMins(this.allBalanceMaxMins);
					break;
				case this.RADIO_VAL_CUSTOM:
					// leave hrs alone
					this.setSelectedScheduledStopInMins(0);
					break;
				default:
					break;
			}
		},
		getMaxHrs (selectedComputedBalance: number, purchasePrice: number) {
			let result: number = 0;
			if (!this.canGoNegative && this.allowAnyTimeSelection) {
				result = this.maxHoursLimit;
			} else if (this.canGoNegative) {
				// For canGoNegative accounts: what they can afford + NEGATIVE_HOURS_LIMIT (the additional time they can go negative)
				if (purchasePrice > 0) {
					const maxHrsDecimal: BigNumber = BigNumber(selectedComputedBalance).div(purchasePrice);
					const maxHrsInt: BigNumber = maxHrsDecimal.integerValue(BigNumber.ROUND_FLOOR);
					const actualMaxHrs: number = Math.max(maxHrsInt.toNumber(), 0);
					result = actualMaxHrs + this.NEGATIVE_HOURS_LIMIT;
				} else {
					result = this.NEGATIVE_HOURS_LIMIT;
				}
			} else if (
				purchasePrice > 0 &&
				selectedComputedBalance > 0
			) {
				const maxHrsDecimal: BigNumber = BigNumber(selectedComputedBalance).div(purchasePrice);
				const maxHrsInt: BigNumber = maxHrsDecimal.integerValue(BigNumber.ROUND_FLOOR);
				result = Math.max(maxHrsInt.toNumber(), 0);
			} else if (purchasePrice === 0) {
				result = 1;
			} else {
				result = 0;
			}
			return Math.min(result, this.maxHoursLimit);
		},
		getMaxMins (
			selectedScheduledStopInHrs: number,
			selectedComputedBalance: number,
			purchasePrice: number,
			incrementUnit: number,
		) {
			if (this.allowAnyTimeSelection) {
				return this.MAX_SUB_60_INCREMENT_UNIT;
			} else if (this.canGoNegative) {
				// For canGoNegative accounts: calculate max minutes based on Math.max(NEGATIVE_HOURS_LIMIT hours, affordable time)
				const maxHrsInt: number = this.getMaxHrs(selectedComputedBalance, purchasePrice);

				if (selectedScheduledStopInHrs >= maxHrsInt) {
					// At max hours, calculate remaining minutes
					if (purchasePrice > 0 && selectedComputedBalance > 0) {
						const maxHrsDecimal: BigNumber = BigNumber(selectedComputedBalance).div(purchasePrice);
						const actualMaxHrs: number = maxHrsDecimal.integerValue(BigNumber.ROUND_FLOOR).toNumber();

						// Only allow extra minutes if we can afford more than the selected hours
						if (actualMaxHrs > selectedScheduledStopInHrs || (actualMaxHrs === selectedScheduledStopInHrs && actualMaxHrs >= this.NEGATIVE_HOURS_LIMIT)) {
							const maxHrsDecimalRemainder: BigNumber = maxHrsDecimal.mod(1);
							const maxMinsDecimal: BigNumber = maxHrsDecimalRemainder.times(60);
							const maxMinsInt: BigNumber = maxMinsDecimal.integerValue(BigNumber.ROUND_FLOOR);
							const maxMinsNearestIncrementInt: BigNumber = maxMinsInt.div(incrementUnit).integerValue(BigNumber.ROUND_FLOOR).times(incrementUnit);
							return Math.max(maxMinsNearestIncrementInt.toNumber(), 0);
						}
					}
					return 0;
				}
				return this.MAX_SUB_60_INCREMENT_UNIT;
			} else if (
				(
					purchasePrice > 0 &&
					selectedComputedBalance > 0
				) ||
				purchasePrice === 0
			) {
				const maxHrsInt: number = this.getMaxHrs(selectedComputedBalance, purchasePrice);
				if (selectedScheduledStopInHrs === maxHrsInt) {
					const maxHrsDecimal: BigNumber = BigNumber(selectedComputedBalance).div(purchasePrice);
					const maxHrsDecimalRemainder: BigNumber = maxHrsDecimal.mod(1);
					const maxMinsDecimal: BigNumber = maxHrsDecimalRemainder.times(60);
					const maxMinsInt: BigNumber = maxMinsDecimal.integerValue(BigNumber.ROUND_FLOOR);
					const maxMinsNearestIncrementInt: BigNumber = maxMinsInt.div(incrementUnit).integerValue(BigNumber.ROUND_FLOOR).times(incrementUnit);
					return Math.max(maxMinsNearestIncrementInt.toNumber(), 0);
				}
				return this.MAX_SUB_60_INCREMENT_UNIT;
			}
			return 0;
		},
		setRadioSelectionsState (selectedComputedBalance: number, purchasePrice: number) {
			const maxHrs: number = this.getMaxHrs(selectedComputedBalance, purchasePrice);
			this.allBalanceMaxHrs = Math.min(maxHrs, this.RADIO_VAL_ALL_MAX_HRS);

			this.allBalanceMaxMins = this.getMaxMins(
				this.allBalanceMaxHrs,
				selectedComputedBalance,
				purchasePrice,
				1,
			);

			/*
			Check if actual usable time with "All available balance" is greater than NEGATIVE_HOURS_LIMIT for canGoNegative accounts
			*/
			let isAllOptionGreaterThanNegativeHoursLimit: boolean = !this.canGoNegative;
			if (this.canGoNegative && purchasePrice > 0 && selectedComputedBalance > 0) {
				const maxHrsDecimal: BigNumber = BigNumber(selectedComputedBalance).div(purchasePrice);
				const actualAffordableHrs: number = maxHrsDecimal.integerValue(BigNumber.ROUND_FLOOR).toNumber();

				if (actualAffordableHrs > this.NEGATIVE_HOURS_LIMIT) {
					isAllOptionGreaterThanNegativeHoursLimit = true;
				} else if (actualAffordableHrs === this.NEGATIVE_HOURS_LIMIT) {
					// Check if there are extra minutes beyond the hour limit
					const maxHrsDecimalRemainder: BigNumber = maxHrsDecimal.mod(1);
					const maxMinsDecimal: BigNumber = maxHrsDecimalRemainder.times(60);
					const actualAffordableMins: number = maxMinsDecimal.integerValue(BigNumber.ROUND_FLOOR).toNumber();
					isAllOptionGreaterThanNegativeHoursLimit = actualAffordableMins > 0;
				} else {
					isAllOptionGreaterThanNegativeHoursLimit = false;
				}
			}
			this.showRadioValAll = !!(
				!this.allowAnyTimeSelection &&
				this.purchasePrice > 0 &&
				this.allBalanceMaxHrs === maxHrs &&
				(
					this.allBalanceMaxHrs > 0 ||
					this.allBalanceMaxMins > 5
				) &&
				(
					!this.canGoNegative ||
					isAllOptionGreaterThanNegativeHoursLimit
				)
			);

			this.showRadioValCustom = !!(
				(
					this.allBalanceMaxHrs > 0 ||
					this.allBalanceMaxMins > this.INCREMENT_UNIT
				)
			);

			const radioGroupValNeedsChange: boolean = !!(
				!this.radioGroupVal ||
				(this.radioGroupVal === this.RADIO_VAL_3_HR && this.allBalanceMaxHrs < 3) ||
				(this.radioGroupVal === this.RADIO_VAL_2_HR && this.allBalanceMaxHrs < 2) ||
				(this.radioGroupVal === this.RADIO_VAL_1_HR && this.allBalanceMaxHrs < 1) ||
				(
					this.radioGroupVal === this.RADIO_VAL_CUSTOM &&
					!this.allBalanceMaxHrs &&
					this.allBalanceMaxMins < this.INCREMENT_UNIT
				)
			);
			if (radioGroupValNeedsChange) {
				if (this.allBalanceMaxHrs >= 2) {
					this.radioGroupVal = this.RADIO_VAL_2_HR;
				} if (this.allBalanceMaxHrs >= 1) {
					this.radioGroupVal = this.RADIO_VAL_1_HR;
				} else if (this.showRadioValAll) {
					this.radioGroupVal = this.RADIO_VAL_ALL;
				} else {
					this.radioGroupVal = this.RADIO_VAL_CUSTOM;
				}
			}
		},
		initScheduledStopInHrsItems (selectedComputedBalance: number, purchasePrice: number) {
			const oldLength = this.scheduledStopInHrsItems.length;
			this.scheduledStopInHrsItems = [{ text: '0', value: 0 }];
			this.showRadioVal1Hr = false;
			this.showRadioVal2Hr = false;
			this.showRadioVal3Hr = false;

			let defaultHrsValue: number = 0;
			const maxHrsInt: number = this.getMaxHrs(selectedComputedBalance, purchasePrice);

			// Fill up select items
			let currHrs: number = 0;
			while (currHrs <= maxHrsInt) {
				// Init shortcut radio group val options
				if (currHrs === 1) {
					this.showRadioVal1Hr = true;
				} else if (currHrs === 2) {
					this.showRadioVal2Hr = true;
				} else if (currHrs === 3) {
					this.showRadioVal3Hr = true;
				}

				// Process next hrs amount
				if (currHrs <= this.highestDefaultValueAllowed && currHrs > defaultHrsValue) {
					defaultHrsValue = currHrs;
				}
				if (currHrs) {
					this.scheduledStopInHrsItems.push({ text: _toString(currHrs), value: currHrs });
				}
				currHrs++;
			}

			// Default selected value
			const foundInItemList: boolean = this.scheduledStopInHrsItems.some(({ value }) => (
				value === this.selectedScheduledStopInHrs
			));
			if (
				this.usingAllBalance &&
				this.selectedScheduledStopInHrs !== this.allBalanceMaxHrs
			) {
				this.setSelectedScheduledStopInHrs(this.allBalanceMaxHrs);
			} else if (
				!this.usingAllBalance &&
				(
					oldLength !== this.scheduledStopInHrsItems.length ||
					!foundInItemList
				)
			) {
				this.setSelectedScheduledStopInHrs(defaultHrsValue);
			}
		},
		initScheduledStopInMinsItems (selectedScheduledStopInHrs: number, selectedComputedBalance: number, purchasePrice: number) {
			const oldLength = this.scheduledStopInMinsItems.length;
			this.scheduledStopInMinsItems = [{ text: '0', value: 0 }];
			const maxMinsInt: number = this.getMaxMins(
				selectedScheduledStopInHrs,
				selectedComputedBalance,
				purchasePrice,
				this.INCREMENT_UNIT,
			);

			// Fill up select items
			let currMins: number = 0;
			while (currMins <= maxMinsInt && currMins <= this.MAX_SUB_60_INCREMENT_UNIT) {
				// Process next mins amount
				if (currMins) {
					this.scheduledStopInMinsItems.push({ text: _toString(currMins), value: currMins });
				}
				currMins += this.INCREMENT_UNIT;
			}

			// Default selected value
			const foundInItemList: boolean = this.scheduledStopInMinsItems.some(({ value }) => (
				value === this.selectedScheduledStopInMins
			));
			if (
				this.usingAllBalance &&
				this.selectedScheduledStopInMins !== this.allBalanceMaxMins
			) {
				this.setSelectedScheduledStopInMins(this.allBalanceMaxMins);
			} else if (
				!this.usingAllBalance &&
				(
					oldLength !== this.scheduledStopInMinsItems.length ||
					!foundInItemList
				)
			) {
				this.setSelectedScheduledStopInMins(0);
			}
		},
		syncRadioGroupVal (selectedScheduledStopInHrs: number, selectedScheduledStopInMins: number) {
			if (
				(this.RADIO_VAL_ALL === this.radioGroupVal && this.showRadioValAll) ||
				(this.RADIO_VAL_CUSTOM === this.radioGroupVal && this.showRadioValCustom)
			) {
				// Don't auto-select away from these options when they're visible, they are always just selected by user clicks
				return;
			}

			const is1HrEvenMark: boolean = selectedScheduledStopInMins === 0 && selectedScheduledStopInHrs === 1;
			const is2HrEvenMark: boolean = selectedScheduledStopInMins === 0 && selectedScheduledStopInHrs === 2;
			const is3HrEvenMark: boolean = selectedScheduledStopInMins === 0 && selectedScheduledStopInHrs === 3;
			const isOtherHrsAmount: boolean = (
				!is1HrEvenMark &&
				!is2HrEvenMark &&
				!is3HrEvenMark
			);

			if (
				is1HrEvenMark &&
				this.radioGroupVal !== this.RADIO_VAL_1_HR
			) {
				this.radioGroupVal = this.RADIO_VAL_1_HR;
			} else if (
				is2HrEvenMark &&
				this.radioGroupVal !== this.RADIO_VAL_2_HR
			) {
				this.radioGroupVal = this.RADIO_VAL_2_HR;
			} else if (
				is3HrEvenMark &&
				this.radioGroupVal !== this.RADIO_VAL_3_HR
			) {
				this.radioGroupVal = this.RADIO_VAL_3_HR;
			} else if (
				isOtherHrsAmount &&
				(selectedScheduledStopInMins % this.INCREMENT_UNIT) === 0 &&
				this.radioGroupVal !== this.RADIO_VAL_CUSTOM
			) {
				this.radioGroupVal = this.RADIO_VAL_CUSTOM;
			}
		},
		setSelectedScheduledStopInHrs (val: number) {
			const valToEmit: number = val || 0;
			if (
				this.usingAllBalance ||
				this.scheduledStopInHrsItems.some(({ value }) => value === valToEmit)
			) {
				this.$emit('selected-scheduled-stop-in-hrs-input', valToEmit);
			}
		},
		setSelectedScheduledStopInMins (val: number) {
			const valToEmit: number = val || 0;
			if (
				this.usingAllBalance ||
				this.scheduledStopInMinsItems.some(({ value }) => value === valToEmit)
			) {
				this.$emit('selected-scheduled-stop-in-mins-input', valToEmit);
			}
		},
	},
	components: {
		InfoMessage,
		TokensSVG,
	},
});
